import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UserDetails from './components/UserDetails/UserDetails';
import AvatarSection from './components/AvatarSection';
import UserRates from './components/UserRates/UserRates';
import UserPortfolio from './components/UserPortfolio/UserPortfolio';
import UserReviews from './components/UserReviews/UserReviews';
import { HandymanAppState } from '../../configuration/redux/reducers';
import GoogleMapsWrapper from './components/UserDetails/GoogleMapsWrapper';
import { AvatarSkeleton } from './skeletons/AvatarSkeleton';
import { UserDetailsSkeleton } from './skeletons/UserDetailsSkeleton';

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role='tabpanel' hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

interface UserProfileProps {}

enum UserProfileTabs {
  ContactInfo = 1,
  Portfolio = 2,
  Rates = 3,
  Reviews = 4,
  Reports = 5,
}

// Tab groups for mobile two-level navigation
const tabGroups = {
  profile: {
    title: 'Profile Information',
    tabs: [UserProfileTabs.ContactInfo, UserProfileTabs.Portfolio],
  },
  business: {
    title: 'Business & Reviews',
    tabs: [
      UserProfileTabs.Rates,
      UserProfileTabs.Reviews,
      UserProfileTabs.Reports,
    ],
  },
};

const UserProfile: React.FC<UserProfileProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useSelector(
    (state: HandymanAppState) => state.user.currentUserDetails,
  );
  const isUserLoading = !currentUser;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Mobile accordion state
  const [expandedGroup, setExpandedGroup] = useState<string | false>(false);

  const getActiveTabFromUrl = (): UserProfileTabs => {
    const params = new URLSearchParams(location.search);
    const tab = parseInt(params.get('activeTab') || '1', 10);
    return Object.values(UserProfileTabs).includes(tab)
      ? tab
      : UserProfileTabs.ContactInfo;
  };

  const [activeTab, setActiveTab] = useState<UserProfileTabs>(
    getActiveTabFromUrl(),
  );

  useEffect(() => {
    const currentTab = getActiveTabFromUrl();
    if (currentTab !== activeTab) {
      setActiveTab(currentTab);
    }
  }, [location.search]);

  const handleChange = (newValue: UserProfileTabs) => {
    setActiveTab(newValue);
    navigate(`/profile?activeTab=${newValue}`);
  };

  // Mobile accordion handlers
  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedGroup(isExpanded ? panel : false);
    };

  // Get tab name for display
  const getTabName = (tab: UserProfileTabs): string => {
    switch (tab) {
      case UserProfileTabs.ContactInfo:
        return 'General';
      case UserProfileTabs.Portfolio:
        return 'Portfolio';
      case UserProfileTabs.Rates:
        return 'Rates';
      case UserProfileTabs.Reviews:
        return 'Reviews';
      case UserProfileTabs.Reports:
        return 'Reports';
      default:
        return '';
    }
  };

  // Check if current tab belongs to a group
  const getCurrentGroup = (): string | null => {
    for (const [groupKey, group] of Object.entries(tabGroups)) {
      if (group.tabs.includes(activeTab)) {
        return groupKey;
      }
    }
    return null;
  };

  // Auto-expand group on mobile when tab changes
  useEffect(() => {
    if (isMobile) {
      const currentGroup = getCurrentGroup();
      if (currentGroup) {
        setExpandedGroup(currentGroup);
      }
    }
  }, [activeTab, isMobile]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: { xs: '10px', md: '10px' },
        gap: { xs: '24px', md: '48px' },
        minHeight: '100vh',
        background: isDark ? '#1A1A1A' : '#E5E5DC',
        paddingTop: { xs: '100px', md: '120px' },
        paddingBottom: { xs: '40px', md: '60px' },
      }}
    >
      {/* Avatar Section Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: { xs: '16px', md: '24px' },
          gap: { xs: '24px', md: '48px' },
          width: { xs: '100%', md: '956px' },
          maxWidth: '95%',
          background: isDark ? '#1A1A1A' : '#E5E5DC',
          boxShadow:
            '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
          borderRadius: { xs: '32px', md: '64px' },
        }}
      >
        {isUserLoading ? <AvatarSkeleton /> : <AvatarSection />}
      </Box>

      {/* Tabs and Content Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '0px',
          width: { xs: '100%', md: '1152px' },
          maxWidth: '95%',
          gap: '0px',
        }}
      >
        {/* Desktop Tabs Row */}
        {!isMobile && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'flex-start',
              padding: '0px',
              gap: '0px',
              width: '100%',
              height: '48px',
            }}
          >
            {/* Desktop Tab Component */}
            {(
              Object.values(UserProfileTabs).filter(
                (value) => typeof value === 'number',
              ) as UserProfileTabs[]
            ).map((tab) => (
              <Box
                key={tab}
                onClick={() => handleChange(tab)}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '8px 32px',
                  gap: '10px',
                  flex: '1',
                  height: '48px',
                  background:
                    activeTab === tab
                      ? isDark
                        ? '#1A1A1A'
                        : '#E5E5DC'
                      : 'transparent',
                  boxShadow:
                    activeTab === tab
                      ? '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF'
                      : 'none',
                  borderRadius: '32px 32px 0px 0px',
                  cursor: 'pointer',
                  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background:
                      activeTab === tab
                        ? isDark
                          ? '#1A1A1A'
                          : '#E5E5DC'
                        : 'rgba(0, 0, 0, 0.02)',
                  },
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'Montserrat',
                    fontStyle: 'normal',
                    fontWeight: 800,
                    fontSize: '20px',
                    lineHeight: '160%',
                    textAlign: 'center',
                    letterSpacing: '0.15px',
                    color: isDark ? '#D5D5D5' : '#585858',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getTabName(tab)}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Mobile Accordion Navigation */}
        {isMobile && (
          <Box sx={{ width: '100%', marginBottom: '16px' }}>
            {Object.entries(tabGroups).map(([groupKey, group]) => (
              <Accordion
                key={groupKey}
                expanded={expandedGroup === groupKey}
                onChange={handleAccordionChange(groupKey)}
                sx={{
                  background: 'transparent',
                  boxShadow: 'none',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '16px !important',
                  marginBottom: '12px',
                  '&:before': {
                    display: 'none',
                  },
                  '&.Mui-expanded': {
                    margin: '0 0 12px 0',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{ color: isDark ? '#00FFFF' : '#005CAD' }}
                    />
                  }
                  sx={{
                    borderRadius: '16px',
                    background: isDark
                      ? 'rgba(0, 92, 173, 0.1)'
                      : 'rgba(0, 36, 58, 0.1)',
                    '&:hover': {
                      background: isDark
                        ? 'rgba(0, 92, 173, 0.2)'
                        : 'rgba(0, 36, 58, 0.2)',
                    },
                    '&.Mui-expanded': {
                      borderRadius: '16px 16px 0 0',
                    },
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: 'Montserrat',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      fontSize: '16px',
                      color: isDark ? '#FFFFFF' : '#1A1A1A',
                    }}
                  >
                    {group.title}
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    background: isDark
                      ? 'rgba(0, 92, 173, 0.05)'
                      : 'rgba(0, 36, 58, 0.05)',
                    borderRadius: '0 0 16px 16px',
                    padding: '8px 16px 16px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  {group.tabs.map((tab) => (
                    <Box
                      key={tab}
                      onClick={() => handleChange(tab)}
                      sx={{
                        padding: '12px 16px',
                        borderRadius: '12px',
                        background:
                          activeTab === tab
                            ? isDark
                              ? 'rgba(0, 92, 173, 0.3)'
                              : 'rgba(0, 36, 58, 0.3)'
                            : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        border:
                          activeTab === tab
                            ? `1px solid ${isDark ? '#00FFFF' : '#005CAD'}`
                            : '1px solid transparent',
                        '&:hover': {
                          background:
                            activeTab === tab
                              ? isDark
                                ? 'rgba(0, 92, 173, 0.4)'
                                : 'rgba(0, 36, 58, 0.4)'
                              : isDark
                                ? 'rgba(0, 92, 173, 0.1)'
                                : 'rgba(0, 36, 58, 0.1)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          fontFamily: 'Montserrat',
                          fontStyle: 'normal',
                          fontWeight: activeTab === tab ? 600 : 500,
                          fontSize: '14px',
                          color:
                            activeTab === tab
                              ? isDark
                                ? '#00FFFF'
                                : '#005CAD'
                              : isDark
                                ? '#D5D5D5'
                                : '#585858',
                        }}
                      >
                        {getTabName(tab)}
                      </Box>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {/* Content Container */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: '0px',
            gap: '10px',
            width: '100%',
            minHeight: { xs: '600px', md: '902px' },
          }}
        >
          <Box
            sx={{
              width: '100%',
              minHeight: { xs: '600px', md: '902px' },
              background: isDark ? '#1A1A1A' : '#E5E5DC',
              boxShadow:
                '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF',
              borderRadius: isMobile
                ? '16px' // Mobile: all corners rounded
                : activeTab === UserProfileTabs.ContactInfo
                  ? '0px 64px 64px 64px' // Desktop: No top-left radius when first tab is active
                  : activeTab === UserProfileTabs.Reports
                    ? '64px 0px 64px 64px' // Desktop: No top-right radius when last tab is active
                    : '64px', // Desktop: All corners rounded for middle tabs
              padding: { xs: '24px 16px', md: '48px 24px 24px 24px' },
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <TabPanel value={activeTab} index={UserProfileTabs.ContactInfo}>
              <GoogleMapsWrapper fallback={<UserDetailsSkeleton />}>
                {isUserLoading ? <UserDetailsSkeleton /> : <UserDetails />}
              </GoogleMapsWrapper>
            </TabPanel>

            <TabPanel value={activeTab} index={UserProfileTabs.Portfolio}>
              <UserPortfolio />
            </TabPanel>

            <TabPanel value={activeTab} index={UserProfileTabs.Rates}>
              <UserRates />
            </TabPanel>

            <TabPanel value={activeTab} index={UserProfileTabs.Reviews}>
              <UserReviews userId={currentUser?.id} />
            </TabPanel>

            <TabPanel value={activeTab} index={UserProfileTabs.Reports}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '400px',
                  fontFamily: 'Roboto',
                  fontSize: '16px',
                  color: isDark ? '#D5D5D5' : '#585858',
                }}
              >
                Reports content coming soon...
              </Box>
            </TabPanel>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UserProfile;
